<template>
  <div class="note-list">
    <el-button
      size="small"
      plain
      type="primary"
      @click="openCreateNewNote"
      :disabled="disabled"
    >
      Add New Shipment Note
    </el-button>
    <div class="list-container f-c">
      <div
        v-for="(row, i) in Array(noteSize)"
        :key="'note-row-' + i"
        class="note-row f-r"
        :class="{ 'last-row': i === noteSize - 1 }"
      >
        <div v-for="j in Two" :key="i + '-note-' + j" class="note f-r">
          <div class="left f-c-vc" v-if="i * 2 + j < notes.length">
            <svg-icon icon-class="note" class="note-icon" />
            <div class="line" />
          </div>
          <div class="right f-c" v-if="i * 2 + j < notes.length">
            <div
              class="note-content"
              :class="{
                important: getNote(i, j).status === NoteImportant
              }"
            >
              <div class="time">
                {{ getNoteCreatedTime(i, j) }}
              </div>
              <div
                class="content"
                :class="{
                  striked: getNote(i, j).status === NoteDeleted
                }"
              >
                <div class="f-r" style="gap: 20px;">
                  <span class="container-no">{{
                    getNoteOrderNo(getNote(i, j))
                  }}</span>
                  <address-tag :address="getNoteAddress(getNote(i, j))" />
                </div>
                <span class="note">{{ getNote(i, j).content }}</span>
              </div>
            </div>
            <div class="status-buts f-r">
              <svg-icon
                icon-class="important"
                @click.native="() => changeStatus(i, j, NoteNormal)"
                v-if="getNote(i, j).status === NoteImportant"
              />
              <svg-icon
                icon-class="unimportant"
                @click.native="() => changeStatus(i, j, NoteImportant)"
                v-if="getNote(i, j).status === NoteNormal"
              />
              <svg-icon
                icon-class="trashed"
                @click.native="
                  () =>
                    changeStatus(
                      i,
                      j,
                      getNote(i, j).status === NoteDeleted
                        ? NoteNormal
                        : NoteDeleted
                    )
                "
                :class="{ deleted: getNote(i, j).status === NoteDeleted }"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <shipment-note-edit ref="newNote" :address-groups="stockList" />
  </div>
</template>
<script>
// models
import { NoteDeleted, NoteNormal, NoteImportant } from '@/core/models/note'
// components
import ShipmentNoteEdit from '@/components/note/ShipmentNoteNew.vue'
import AddressTag from '../address/AddressTag.vue'
import { searchStock } from '@/api/pallet'
import { queryAddress } from '@/api/address'
import { modifyShipment } from '@/api/shipment'
import PagesMessage from '@/core/message/pages'
export default {
  name: 'ShipmentNoteList',
  props: {
    shipmentId: {
      type: String,
      required: true
    },
    notes: {
      type: Array,
      required: true
    },
    changeCb: {
      type: Function,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  components: {
    ShipmentNoteEdit,
    AddressTag
  },
  data() {
    return {
      NoteDeleted,
      NoteNormal,
      NoteImportant,
      stockList: [],
      Two: [0, 1]
    }
  },
  computed: {
    noteSize() {
      return Math.ceil(this.notes.length / 2)
    }
  },
  methods: {
    getNoteOrderNo(note) {
      const group = this.stockList.find(
        one => one.orderId === note.orderId && one.address.id === note.addressId
      )
      return group?.riiOrderNo
    },
    getNoteAddress(note) {
      const group = this.stockList.find(
        one => one.orderId === note.orderId && one.address.id === note.addressId
      )
      return group?.address
    },
    getNote(i, j) {
      return this.notes[i * 2 + j]
    },
    getNoteCreatedTime(i, j) {
      const note = this.getNote(i, j)
      const date = new Date(note.createdTime)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
    },
    changeStatus(i, j, status) {
      if (this.disabled) return
      const note = this.getNote(i, j)
      note.status = status
      this.changeCb(this.notes)
    },
    openCreateNewNote() {
      const changeCb = (newNote, done, error) => {
        const notes = [...this.notes, newNote]
        modifyShipment(this.shipmentId, {
          notes
        })
          .then(res => {
            done()
            PagesMessage.emit(PagesMessage.eventMap.REFRESH_SHIPMENT_DETAIL)
          })
          .catch(err => error(err.toString()))
      }
      this.$refs.newNote.open(changeCb)
    },
    createNewNote() {},
    noteValid(index) {
      return index < this.notes.length
    },
    async fetchStock() {
      const stockPage = await searchStock({
        shipmentId: this.shipmentId,
        onlyShipped: true,
        size: -1
      })
      const addressIds = stockPage.records.map(one => one.addressId)
      if (addressIds.length > 0) {
        const addressPage = await queryAddress({ addressIds, size: -1 })
        const addressMap = Object.fromEntries(
          addressPage.records.map(one => [one.id, one])
        )
        stockPage.records.forEach(one => {
          one.address = addressMap[one.addressId]
          one.key = one.orderId + '-' + one.address.id
        })
      }
      this.stockList = stockPage.records
    }
  },
  mounted() {
    // fetch stock
    this.fetchStock()
  }
}
</script>
<style lang="scss" scoped>
@import '@/assets/styles/common.scss';

.note-list {
  margin-bottom: 20px;
}

.note {
  width: 100%;
  gap: 10px;
  align-items: stretch;

  .left {
    height: 100%;

    .note-icon {
      width: 20px;
      height: 20px;
      color: $el-primary-color;
    }

    .line {
      border: 1px solid #ccc;
      flex-grow: 1;
      height: calc(100% - 20px);
    }
  }

  .right {
    flex-grow: 1;
    gap: 5px;
    margin-bottom: 15px;

    &.last-row {
      margin-bottom: 0;
    }

    .note-content {
      background-color: rgba($color: $note-normal-color, $alpha: 1);
      border-radius: 8px;
      padding: $home-card-padding-y $home-card-padding-x;

      .time {
        font-size: 12px;
        margin-bottom: 5px;
      }

      .content {
        .note {
          word-break: break-word;
          font-weight: bold;
        }
      }
    }

    .status-buts {
      margin-left: 10px;
      gap: 5px;

      .svg-icon {
        padding: 3px;

        &:hover {
          cursor: pointer;
          background-color: rgba($color: $el-primary-color, $alpha: 0.1);
          border-radius: 4px;
        }
      }
    }
  }
}

.deleted {
  border-radius: 4px;
  background-color: rgba($color: $el-primary-color, $alpha: 0.1);
}

.striked {
  text-decoration: line-through;
}

.important {
  background-color: rgba($color: $el-danger-color, $alpha: 0.3) !important;
}

.list-container {
  margin-top: 10px;

  .note-row {
    align-items: stretch;
    gap: 20px;

    &.last-row ::v-deep .right {
      margin-bottom: 0;
    }
  }
}
</style>
